import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const WEBINAR_RECORDINGS_BUCKET = 'webinar-recordings';

const WebinarFormModal = ({ isOpen, onClose, onSubmit, webinar, experts }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    expert_id: '',
    scheduled_at: '',
    duration_minutes: 60,
    status: 'upcoming',
    topic: '',
    max_participants: 500,
    meeting_url: '',
    recording_url: '',
    thumbnail_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Recording upload state
  const [recordingInputMode, setRecordingInputMode] = useState('url'); // 'url' | 'upload'
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (webinar) {
      setFormData({
        title: webinar?.title || '',
        description: webinar?.description || '',
        expert_id: webinar?.expert_id || '',
        scheduled_at: webinar?.scheduled_at ? new Date(webinar?.scheduled_at)?.toISOString()?.slice(0, 16) : '',
        duration_minutes: webinar?.duration_minutes || 60,
        status: webinar?.status || 'upcoming',
        topic: webinar?.topic || '',
        max_participants: webinar?.max_participants || 500,
        meeting_url: webinar?.meeting_url || '',
        recording_url: webinar?.recording_url || '',
        thumbnail_url: webinar?.thumbnail_url || ''
      });
    } else {
      setFormData({
        title: '',
        description: '',
        expert_id: '',
        scheduled_at: '',
        duration_minutes: 60,
        status: 'upcoming',
        topic: '',
        max_participants: 500,
        meeting_url: '',
        recording_url: '',
        thumbnail_url: ''
      });
    }
    setError('');
    setUploadFile(null);
    setUploadProgress(0);
    setUploadError('');
    setRecordingInputMode('url');
  }, [webinar, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file?.size > maxSize) {
      setUploadError('File size exceeds 500MB limit');
      return;
    }

    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
    if (!allowedTypes?.includes(file?.type)) {
      setUploadError('Only MP4, WebM, OGG, and MOV video files are supported');
      return;
    }

    setUploadFile(file);
    setUploadError('');
  };

  const handleUploadVideo = async () => {
    if (!uploadFile) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      const timestamp = Date.now();
      const fileExt = uploadFile?.name?.split('.')?.pop();
      const safeTitle = (formData?.title || 'webinar')?.replace(/[^a-zA-Z0-9]/g, '-')?.toLowerCase();
      const fileName = `${safeTitle}-${timestamp}.${fileExt}`;
      const filePath = `recordings/${fileName}`;

      const { data, error: uploadErr } = await supabase?.storage
        ?.from(WEBINAR_RECORDINGS_BUCKET)
        ?.upload(filePath, uploadFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase?.storage
        ?.from(WEBINAR_RECORDINGS_BUCKET)
        ?.getPublicUrl(filePath);

      setFormData((prev) => ({ ...prev, recording_url: urlData?.publicUrl }));
      setUploadFile(null);
      setUploadProgress(100);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err?.message || 'Failed to upload video. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.title || !formData?.expert_id || !formData?.scheduled_at) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        scheduled_at: new Date(formData?.scheduled_at)?.toISOString(),
        duration_minutes: parseInt(formData?.duration_minutes),
        max_participants: parseInt(formData?.max_participants)
      };
      await onSubmit(submitData);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save webinar');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const statusOptions = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'live', label: 'Live' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const expertOptions = experts?.map((expert) => ({
    value: expert?.id,
    label: expert?.name
  })) || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            {webinar ? 'Edit Webinar' : 'Create Webinar'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Title <span className="text-error">*</span>
            </label>
            <Input
              name="title"
              value={formData?.title}
              onChange={handleChange}
              placeholder="Enter webinar title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleChange}
              placeholder="Enter webinar description"
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Expert <span className="text-error">*</span>
              </label>
              <Select
                value={formData?.expert_id}
                onChange={(value) => handleSelectChange('expert_id', value)}
                options={expertOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Topic
              </label>
              <Input
                name="topic"
                value={formData?.topic}
                onChange={handleChange}
                placeholder="e.g., Artificial Intelligence"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Scheduled Date & Time <span className="text-error">*</span>
              </label>
              <Input
                type="datetime-local"
                name="scheduled_at"
                value={formData?.scheduled_at}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Duration (minutes)
              </label>
              <Input
                type="number"
                name="duration_minutes"
                value={formData?.duration_minutes}
                onChange={handleChange}
                min="15"
                step="15"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <Select
                value={formData?.status}
                onChange={(value) => handleSelectChange('status', value)}
                options={statusOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Max Participants
              </label>
              <Input
                type="number"
                name="max_participants"
                value={formData?.max_participants}
                onChange={handleChange}
                min="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Meeting URL
            </label>
            <Input
              name="meeting_url"
              value={formData?.meeting_url}
              onChange={handleChange}
              placeholder="https://meet.example.com/session"
            />
          </div>

          {/* Recording Section */}
          <div className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="PlayCircle" size={16} color="var(--color-primary)" />
              <label className="text-sm font-medium text-foreground">
                Recording (for completed sessions)
              </label>
            </div>

            {/* Toggle between URL and Upload */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRecordingInputMode('url')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                  recordingInputMode === 'url' ?'bg-primary text-white border-primary' :'bg-background text-muted-foreground border-border hover:border-primary'
                }`}
              >
                <Icon name="Link" size={13} />
                YouTube / Vimeo URL
              </button>
              <button
                type="button"
                onClick={() => setRecordingInputMode('upload')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                  recordingInputMode === 'upload' ?'bg-primary text-white border-primary' :'bg-background text-muted-foreground border-border hover:border-primary'
                }`}
              >
                <Icon name="Upload" size={13} />
                Upload Video File
              </button>
            </div>

            {recordingInputMode === 'url' ? (
              <div>
                <Input
                  name="recording_url"
                  value={formData?.recording_url}
                  onChange={handleChange}
                  placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Paste a YouTube or Vimeo link — it will be embedded inside the platform for students.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Current recording URL display */}
                {formData?.recording_url && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-success/10 border border-success/30 rounded-lg">
                    <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                    <span className="text-xs text-success font-medium flex-1 truncate">
                      Recording uploaded
                    </span>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, recording_url: '' }))}
                      className="text-xs text-muted-foreground hover:text-error transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                )}

                {/* File picker */}
                <div
                  className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef?.current?.click()}
                >
                  <Icon name="Upload" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                  <p className="text-sm text-foreground font-medium">
                    {uploadFile ? uploadFile?.name : 'Click to select video file'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    MP4, WebM, MOV — max 500MB
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {uploadError && (
                  <p className="text-xs text-error">{uploadError}</p>
                )}

                {uploadFile && !uploading && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="Upload"
                    iconPosition="left"
                    onClick={handleUploadVideo}
                  >
                    Upload to Supabase Storage
                  </Button>
                )}

                {uploading && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="bg-primary h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Video will be stored in Supabase Storage and streamed directly inside the platform.
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Thumbnail URL
            </label>
            <Input
              name="thumbnail_url"
              value={formData?.thumbnail_url}
              onChange={handleChange}
              placeholder="https://images.example.com/thumbnail.jpg"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={loading || uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              disabled={loading || uploading}
            >
              {loading ? 'Saving...' : webinar ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebinarFormModal;